import { WorkPage } from '../components/work-page.tsx';
import { TableScroll } from '@web/components/table-scroll.tsx';
import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { photoQuoteApi, templateApi } from '../api/endpoints.ts';
import { useSession } from '../state/session.tsx';
import { PlanRequiredPage, usePlanFeature } from '../components/plan-gate.tsx';
import { useAiDisclosure } from '../components/ai-disclosure.tsx';
import {
  Alert,
  Button,
  Checkbox,
  ConfirmDialog,
  LoadingBlock,
  Select,
  TextArea,
  TextInput,
} from '../components/ui.tsx';
import AddressLink from '../components/address-link.tsx';
import {
  PhotoGrid,
  RiskBadge,
  SlaTimer,
  homeLine,
  money,
  type ViewerPhoto,
} from '../components/photo-quotes/parts.tsx';
import { PhotoIntakeStatusBadge } from './photo-quotes.tsx';
import { useDocumentTitle, useOnlineStatus } from '../hooks/index.ts';
import { formatMinutes } from '../i18n/format.ts';
import { ROOM_CONDITION_CODES, ROOM_SIZE_CODES } from '@shared/constants/index.ts';
import {
  MAX_SQUARE_FEET,
  PHOTO_QUOTE_ITEMS,
  PHOTO_QUOTE_RISK_WEIGHTS,
  type ItemFindingValue,
  type PhotoQuotePriceKind,
  type PhotoQuoteRiskComponent,
  type RoomFindingValue,
} from '@shared/photo-quotes.ts';
import { PHOTO_REQUEST_NEEDS, type PhotoRequestNeed } from '@shared/schemas/photo-quotes.ts';
import type { PhotoIntakeDetail, PhotoIntakeFinding } from '@shared/types/photo-quotes.ts';

/**
 * One photo quote, for the office to decide (spec WILD-05, "Owner review").
 *
 * Photos on one side, what was read from them on the other, and the price the
 * engine gives for exactly what is on screen. Every correction goes to the
 * server and comes back re-priced and re-scored — the number shown is never
 * the browser's arithmetic. The four ways out are the spec's: a firm price, a
 * range, more photos, or a visit.
 */
export default function PhotoQuoteReviewPage() {
  const { t } = useTranslation();
  const { company } = useSession();
  const hasQueue = usePlanFeature('magic_quote');
  if (company && !company.photoQuotesEnabled) return <Navigate to="/requests" replace />;
  if (!hasQueue) return <PlanRequiredPage feature="magic_quote" title={t('photoQuotes.review.title')} />;
  return <PhotoQuoteReview />;
}

type Dialog =
  { kind: 'send'; price: PhotoQuotePriceKind } | { kind: 'more' } | { kind: 'walkthrough' } | { kind: 'reread' };

function PhotoQuoteReview() {
  const { t } = useTranslation();
  const { intakeId = '' } = useParams();
  const { can, company } = useSession();
  const online = useOnlineStatus();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<{ quoteId: string; emailed: boolean } | null>(null);

  const query = useQuery({
    queryKey: ['photo-intake', intakeId],
    queryFn: () => photoQuoteApi.get(intakeId),
    // While the photos are being read the page watches for the result.
    refetchInterval: (state) => (state.state.data?.status === 'analyzing' ? 3_000 : false),
  });
  const intake = query.data;
  useDocumentTitle(
    intake ? t('photoQuotes.review.documentTitle', { name: intake.name }) : t('photoQuotes.review.title'),
  );

  const canWrite = can('quotes:write');
  const canSend = canWrite && can('customers:write');
  // The intake's own priced currency — its location's, which may not be the
  // company's (0114) — whenever there is a preview to say so.
  const currency = intake?.preview?.currencyCode ?? company?.currencyCode ?? 'USD';

  const apply = (detail: PhotoIntakeDetail) => {
    queryClient.setQueryData(['photo-intake', intakeId], detail);
    void queryClient.invalidateQueries({ queryKey: ['photo-intakes'] });
    void queryClient.invalidateQueries({ queryKey: ['photo-intakes-count'] });
    void queryClient.invalidateQueries({ queryKey: ['quote-requests'] });
    void queryClient.invalidateQueries({ queryKey: ['quote-requests-new-count'] });
  };
  const onError = (mutationError: Error) => {
    setError(mutationError.message);
    // A stale version means somebody else changed it: show the truth.
    void query.refetch();
  };

  const edit = useMutation({
    mutationFn: ({ finding, value }: { finding: PhotoIntakeFinding; value: Record<string, unknown> }) =>
      photoQuoteApi.editFinding(intakeId, finding.id, { version: intake!.findingsVersion, value }),
    onSuccess: (detail) => {
      setError(null);
      apply(detail);
    },
    onError,
  });

  const addRoom = useMutation({
    mutationFn: (value: RoomFindingValue) =>
      photoQuoteApi.addRoom(intakeId, { version: intake!.findingsVersion, value }),
    onSuccess: (detail) => {
      setError(null);
      apply(detail);
    },
    onError,
  });

  if (query.isLoading) return <LoadingBlock label={t('photoQuotes.review.loading')} />;
  if (query.isError || !intake) {
    return (
      <Alert tone="error" title={t('photoQuotes.review.loadErrorTitle')}>
        {(query.error as Error | null)?.message ?? ''}
      </Alert>
    );
  }

  const reviewable = intake.status === 'awaiting_owner' || intake.status === 'needs_more_photos';
  const editable = reviewable && canWrite && online && !edit.isPending && !addRoom.isPending;
  const rooms = intake.findings.filter((finding) => finding.kind === 'room');
  const condition = intake.findings.find((finding) => finding.kind === 'condition');
  const squareFeet = intake.findings.find((finding) => finding.kind === 'sqft');
  const items = intake.findings.filter((finding) => finding.kind === 'item');
  const photoLabel = (id: string) => {
    const photo = intake.photos.find((candidate) => candidate.id === id);
    return photo ? t('photoQuotes.photos.label', { position: photo.position }) : null;
  };
  const viewer: ViewerPhoto[] = intake.photos.map((photo) => ({
    id: photo.id,
    src: photo.url,
    label: photo.tags?.descriptor
      ? `${t('photoQuotes.photos.label', { position: photo.position })} · ${photo.tags.descriptor}`
      : t('photoQuotes.photos.label', { position: photo.position }),
    hidden: photo.hiddenReason !== null,
    hiddenReason: photo.hiddenReason,
  }));
  const home = homeLine(t, intake.home);
  // A room the library cannot price would be missing from the quote, so
  // nothing goes out until each one is priced or taken out (count 0).
  const unpricedRooms = intake.preview?.rooms.some((room) => room.unpriced !== null) ?? false;
  const sendable = reviewable && intake.preview !== null && intake.preview.totalCents > 0 && !unpricedRooms;

  return (
    <WorkPage
      title={intake.name}
      description={t('photoQuotes.review.description', { count: intake.photoCount })}
      actions={
        <Link to="/quotes/photos" className="btn btn-secondary">
          {t('photoQuotes.review.back')}
        </Link>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <PhotoIntakeStatusBadge status={intake.status} />
        <RiskBadge level={intake.riskLevel} />
        <SlaTimer dueAt={intake.slaDueAt} status={intake.status} />
        {intake.outOfHours && intake.status === 'awaiting_owner' ? (
          <span className="text-sm text-[color:var(--color-muted)]">{t('photoQuotes.review.outOfHours')}</span>
        ) : null}
      </div>

      {error ? (
        <div className="mb-4">
          <Alert tone="error" title={t('photoQuotes.review.actionErrorTitle')}>
            {error}
          </Alert>
        </div>
      ) : null}
      {sent ? (
        <div className="mb-4">
          <Alert tone="success" title={t('photoQuotes.review.sentTitle')}>
            <p>{sent.emailed ? t('photoQuotes.review.sentEmailed') : t('photoQuotes.review.sentNotEmailed')}</p>
            <p className="mt-1">
              <Link to={`/quotes/${sent.quoteId}`} className="font-semibold underline">
                {t('photoQuotes.queue.openQuote')}
              </Link>
            </p>
          </Alert>
        </div>
      ) : null}

      {intake.status === 'analyzing' ? (
        <div className="mb-4">
          <Alert tone="info">{t('photoQuotes.review.reading')}</Alert>
        </div>
      ) : null}
      {intake.status === 'needs_more_photos' ? (
        <div className="mb-4">
          <Alert tone="info">{t('photoQuotes.review.waitingForPhotos', { count: intake.morePhotoRequestsLeft })}</Alert>
        </div>
      ) : null}
      {intake.status === 'sent' && intake.quoteId ? (
        <div className="mb-4">
          <Alert tone="success">
            <p>
              {intake.priceKind === 'range' && intake.rangeLowCents !== null && intake.rangeHighCents !== null
                ? t('photoQuotes.queue.sentRange', {
                    low: money(intake.rangeLowCents, currency),
                    high: money(intake.rangeHighCents, currency),
                  })
                : t('photoQuotes.queue.sentFirm', { amount: money(intake.priceCents ?? 0, currency) })}
            </p>
            <p className="mt-1">
              <Link to={`/quotes/${intake.quoteId}`} className="font-semibold underline">
                {t('photoQuotes.queue.openQuote')}
              </Link>
            </p>
          </Alert>
        </div>
      ) : null}
      {intake.status === 'closed' && intake.closedReason ? (
        <div className="mb-4">
          <Alert tone="info">{t(`photoQuotes.closed.${intake.closedReason}`)}</Alert>
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* ---- The photos --------------------------------------------------- */}
        <section className="card" aria-labelledby="photo-quote-photos">
          <h2 id="photo-quote-photos" className="mb-3 text-lg font-semibold">
            {t('photoQuotes.review.photosHeading')}
          </h2>
          <PhotoGrid photos={viewer} />
          {intake.photos.some((photo) => photo.hiddenReason) ? (
            <p className="mt-3 text-sm text-[color:var(--color-muted)]">{t('photoQuotes.review.hiddenHint')}</p>
          ) : null}

          <h3 className="mt-6 mb-2 font-semibold">{t('photoQuotes.review.customerHeading')}</h3>
          <dl className="grid grid-cols-1 gap-x-4 gap-y-1 break-words text-sm sm:grid-cols-[minmax(0,auto)_minmax(0,1fr)]">
            {intake.email ? (
              <>
                <dt className="text-[color:var(--color-muted)]">{t('photoQuotes.review.email')}</dt>
                <dd>{intake.email}</dd>
              </>
            ) : null}
            {intake.phone ? (
              <>
                <dt className="text-[color:var(--color-muted)]">{t('photoQuotes.review.phone')}</dt>
                <dd>{intake.phone}</dd>
              </>
            ) : null}
            {intake.address ? (
              <>
                <dt className="text-[color:var(--color-muted)]">{t('photoQuotes.review.address')}</dt>
                <dd>
                  <AddressLink address={intake.address} />
                </dd>
              </>
            ) : null}
            {intake.statedBedrooms !== null || intake.statedBathrooms !== null ? (
              <>
                <dt className="text-[color:var(--color-muted)]">{t('photoQuotes.review.theySaid')}</dt>
                <dd>
                  {[
                    intake.statedBedrooms !== null
                      ? t('photoQuotes.home.bedrooms', { count: intake.statedBedrooms })
                      : null,
                    intake.statedBathrooms !== null
                      ? t('photoQuotes.home.bathrooms', { count: intake.statedBathrooms })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </dd>
              </>
            ) : null}
          </dl>
          {intake.propertyNote ? (
            <blockquote className="mt-3 border-l-4 border-[color:var(--color-line)] pl-3 text-sm text-[color:var(--color-muted)]">
              {intake.propertyNote}
            </blockquote>
          ) : null}
        </section>

        {/* ---- What was read, and the price --------------------------------- */}
        <section className="space-y-6">
          <div className="card">
            <h2 className="mb-2 text-lg font-semibold">{t('photoQuotes.review.readHeading')}</h2>
            {intake.aiSummary ? <p className="mb-2">{intake.aiSummary}</p> : null}
            {home ? <p className="mb-3 text-sm text-[color:var(--color-muted)]">{home}</p> : null}
            {intake.aiSkipReason ? (
              <Alert tone="warning">
                <p>{t(`photoQuotes.skip.${intake.aiSkipReason}`)}</p>
                {/* Whatever stopped the read may have cleared since (an upgrade, AI
                    switched on, rooms added, credits back); the server says if not.
                    Photos sent without the AI notice are never read. */}
                {intake.status === 'awaiting_owner' && canWrite && intake.aiSkipReason !== 'notice' ? (
                  <p className="mt-2">
                    <Button onClick={() => setDialog({ kind: 'reread' })}>{t('photoQuotes.review.reread')}</Button>
                  </p>
                ) : null}
              </Alert>
            ) : null}

            {rooms.length > 0 ? (
              <ul className="divide-y divide-[color:var(--color-line)]">
                {rooms.map((finding) => (
                  <RoomRow
                    key={finding.id}
                    finding={finding}
                    editable={editable}
                    photoLabel={photoLabel}
                    onChange={(value) => edit.mutate({ finding, value })}
                  />
                ))}
              </ul>
            ) : null}

            {condition ? (
              <div className="mt-3 max-w-xs">
                <Select
                  label={t('photoQuotes.review.overallCondition')}
                  value={String(condition.value.conditionCode ?? 'average')}
                  disabled={!editable}
                  options={ROOM_CONDITION_CODES.map((code) => ({ value: code, label: t(`room.condition.${code}`) }))}
                  onChange={(event) =>
                    edit.mutate({ finding: condition, value: { conditionCode: event.target.value } })
                  }
                />
              </div>
            ) : null}

            {squareFeet ? (
              <SquareFeetRow
                key={`${squareFeet.id}:${String(squareFeet.value.low)}:${String(squareFeet.value.high)}`}
                finding={squareFeet}
                editable={editable}
                photoLabel={photoLabel}
                onSave={(value) => edit.mutate({ finding: squareFeet, value })}
              />
            ) : null}

            {items.length > 0 ? (
              <>
                <h3 className="mt-3 mb-1 font-semibold">{t('photoQuotes.review.itemsHeading')}</h3>
                <ul className={editable ? 'divide-y divide-[color:var(--color-line)]' : 'list-disc pl-5 text-sm'}>
                  {items.map((finding) => (
                    <ItemRow
                      key={finding.id}
                      finding={finding}
                      editable={editable}
                      photoLabel={photoLabel}
                      onChange={(value) => edit.mutate({ finding, value })}
                    />
                  ))}
                </ul>
              </>
            ) : null}

            {editable ? <AddRoomForm busy={addRoom.isPending} onAdd={(value) => addRoom.mutate(value)} /> : null}
          </div>

          {intake.preview ? (
            <div className="card">
              <h2 className="mb-2 text-lg font-semibold">{t('photoQuotes.review.priceHeading')}</h2>
              <TableScroll aria-label={t('photoQuotes.review.priceHeading')}>
                <table className="data-table w-full min-w-max text-sm">
                  <tbody>
                    {intake.preview.rooms.map((room) => (
                      <tr key={room.findingId} className="border-b border-[color:var(--color-line)]">
                        <td className="py-1">
                          {room.quantity > 1 ? `${room.quantity} × ` : ''}
                          {room.name}
                          {room.unpriced ? (
                            <span className="ml-2 text-[color:var(--color-danger)]">
                              {t(`photoQuotes.review.unpriced.${room.unpriced}`)}
                            </span>
                          ) : null}
                        </td>
                        <td className="py-1 text-right text-[color:var(--color-muted)]">
                          {room.minutes > 0 ? formatMinutes(room.minutes) : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
              <p className="mt-3 text-2xl font-semibold">
                {money(intake.preview.totalCents, intake.preview.currencyCode)}
              </p>
              <p className="text-sm text-[color:var(--color-muted)]">
                {t('photoQuotes.review.rangeLine', {
                  low: money(intake.preview.range.lowCents, intake.preview.currencyCode),
                  high: money(intake.preview.range.highCents, intake.preview.currencyCode),
                })}
                {intake.preview.estimatedMinutes > 0
                  ? ` · ${t('photoQuotes.review.timeLine', { time: formatMinutes(intake.preview.estimatedMinutes) })}`
                  : ''}
              </p>
            </div>
          ) : null}

          {intake.risk ? <RiskPanel intake={intake} /> : null}

          {reviewable && canWrite ? (
            <div className="card">
              <h2 className="mb-3 text-lg font-semibold">{t('photoQuotes.review.decideHeading')}</h2>
              {!online ? <p className="mb-2 text-sm">{t('photoQuotes.review.offline')}</p> : null}
              {unpricedRooms ? (
                <p className="mb-2 text-sm text-[color:var(--color-danger)]">
                  {t('photoQuotes.review.unpricedBlocksSend')}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  disabled={!sendable || !canSend || !online}
                  onClick={() => setDialog({ kind: 'send', price: 'firm' })}
                >
                  {t('photoQuotes.send.firm')}
                </Button>
                <Button
                  disabled={!sendable || !canSend || !online}
                  onClick={() => setDialog({ kind: 'send', price: 'range' })}
                >
                  {t('photoQuotes.send.range')}
                </Button>
                <Button
                  disabled={!online || intake.morePhotoRequestsLeft === 0 || !intake.email}
                  onClick={() => setDialog({ kind: 'more' })}
                >
                  {t('photoQuotes.review.askMore')}
                </Button>
                <Button disabled={!online} onClick={() => setDialog({ kind: 'walkthrough' })}>
                  {t('photoQuotes.review.walkthrough')}
                </Button>
              </div>
              {intake.morePhotoRequestsLeft === 0 ? (
                <p className="mt-2 text-sm text-[color:var(--color-muted)]">{t('photoQuotes.review.noMoreAsks')}</p>
              ) : null}
            </div>
          ) : null}
        </section>
      </div>

      {dialog?.kind === 'send' && intake.preview ? (
        <SendDialog
          intake={intake}
          kind={dialog.price}
          onCancel={() => setDialog(null)}
          onSent={(result) => {
            setDialog(null);
            setSent({ quoteId: result.quoteId, emailed: result.emailed });
            apply(result.intake);
            void queryClient.invalidateQueries({ queryKey: ['quotes'] });
          }}
        />
      ) : null}
      {dialog?.kind === 'more' ? (
        <AskMoreDialog
          intake={intake}
          onCancel={() => setDialog(null)}
          onDone={(detail) => (setDialog(null), apply(detail))}
        />
      ) : null}
      {dialog?.kind === 'walkthrough' ? (
        <WalkthroughDialog
          intake={intake}
          onCancel={() => setDialog(null)}
          onDone={(detail) => (setDialog(null), apply(detail))}
        />
      ) : null}
      {dialog?.kind === 'reread' ? (
        <RereadDialog
          intake={intake}
          onCancel={() => setDialog(null)}
          onDone={(detail) => (setDialog(null), apply(detail))}
        />
      ) : null}
    </WorkPage>
  );
}

/**
 * The size of the home as the photos suggest it, a band in square feet. It
 * feeds the risk score (a wide band is a less certain home), so the owner can
 * correct it like any other finding.
 */
function SquareFeetRow({
  finding,
  editable,
  photoLabel,
  onSave,
}: {
  finding: PhotoIntakeFinding;
  editable: boolean;
  photoLabel: (id: string) => string | null;
  onSave: (value: { low: number; high: number }) => void;
}) {
  const { t } = useTranslation();
  const storedLow = Number(finding.value.low ?? 0);
  const storedHigh = Number(finding.value.high ?? 0);
  const [low, setLow] = useState(String(storedLow));
  const [high, setHigh] = useState(String(storedHigh));
  const lowValue = Number(low);
  const highValue = Number(high);
  const valid =
    low.trim() !== '' &&
    high.trim() !== '' &&
    Number.isInteger(lowValue) &&
    Number.isInteger(highValue) &&
    lowValue >= 0 &&
    highValue >= lowValue &&
    highValue <= MAX_SQUARE_FEET;
  const changed = lowValue !== storedLow || highValue !== storedHigh;
  return (
    <div className="mt-3 max-w-md">
      <p className="mb-1 font-semibold">{t('photoQuotes.review.squareFeet')}</p>
      <div className="flex flex-wrap items-end gap-2">
        <TextInput
          label={t('photoQuotes.review.squareFeetLow')}
          type="number"
          min={0}
          max={MAX_SQUARE_FEET}
          value={low}
          disabled={!editable}
          onChange={(event) => setLow(event.target.value)}
        />
        <TextInput
          label={t('photoQuotes.review.squareFeetHigh')}
          type="number"
          min={0}
          max={MAX_SQUARE_FEET}
          value={high}
          disabled={!editable}
          onChange={(event) => setHigh(event.target.value)}
        />
        {editable && changed ? (
          <Button disabled={!valid} onClick={() => onSave({ low: lowValue, high: highValue })}>
            {t('photoQuotes.review.squareFeetSave')}
          </Button>
        ) : null}
      </div>
      {editable && changed && !valid ? (
        <p className="text-sm text-[color:var(--color-danger)]">{t('photoQuotes.review.squareFeetInvalid')}</p>
      ) : null}
      <EvidenceChips ids={finding.evidencePhotoIds} photoLabel={photoLabel} />
    </div>
  );
}

/**
 * Something the photos called out that changes the work (pets, clutter, an
 * oven to do). Corrected like any finding, or taken off when the photos got
 * it wrong: the ones left go into the quote's notes for the office and the
 * crew.
 */
function ItemRow({
  finding,
  editable,
  photoLabel,
  onChange,
}: {
  finding: PhotoIntakeFinding;
  editable: boolean;
  photoLabel: (id: string) => string | null;
  onChange: (value: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  const value = finding.value as unknown as ItemFindingValue;
  const name = t(`photoQuotes.items.${value.item}`);
  if (!editable) {
    if (value.dismissed) return null;
    return (
      <li>
        {name}
        {value.note ? `: ${value.note}` : ''}
        <EvidenceChips ids={finding.evidencePhotoIds} photoLabel={photoLabel} />
      </li>
    );
  }
  if (value.dismissed) {
    return (
      <li className="py-2 text-sm text-[color:var(--color-muted)]">
        <span className="line-through">{name}</span> ({t('photoQuotes.review.itemRemoved')})
        <Button className="ml-2" onClick={() => onChange({ ...value, dismissed: false })}>
          {t('photoQuotes.review.restoreItem')}
        </Button>
      </li>
    );
  }
  return (
    <li className="py-2">
      <div className="flex flex-wrap items-end gap-2">
        <Select
          label={t('photoQuotes.review.itemType')}
          value={value.item}
          options={PHOTO_QUOTE_ITEMS.map((item) => ({ value: item, label: t(`photoQuotes.items.${item}`) }))}
          onChange={(event) => onChange({ ...value, item: event.target.value })}
        />
        <TextInput
          // Re-mounted when the stored note changes, like the room counts.
          key={value.note}
          label={t('photoQuotes.review.itemNote')}
          maxLength={240}
          defaultValue={value.note}
          onBlur={(event) => {
            const note = event.target.value.trim();
            if (note !== value.note) onChange({ ...value, note });
          }}
        />
        <Button
          aria-label={t('photoQuotes.review.removeItem', { name })}
          onClick={() => onChange({ ...value, dismissed: true })}
        >
          ×
        </Button>
      </div>
      <EvidenceChips ids={finding.evidencePhotoIds} photoLabel={photoLabel} />
    </li>
  );
}

function EvidenceChips({ ids, photoLabel }: { ids: string[]; photoLabel: (id: string) => string | null }) {
  const labels = ids.map(photoLabel).filter((label): label is string => label !== null);
  if (labels.length === 0) return null;
  return (
    <span className="ml-2 inline-flex flex-wrap gap-1 align-middle">
      {labels.map((label) => (
        <span key={label} className="badge text-xs text-[color:var(--color-muted)]">
          {label}
        </span>
      ))}
    </span>
  );
}

/** One room the photos showed: how many, how big, how dirty — each a re-price away. */
function RoomRow({
  finding,
  editable,
  photoLabel,
  onChange,
}: {
  finding: PhotoIntakeFinding;
  editable: boolean;
  photoLabel: (id: string) => string | null;
  onChange: (value: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  const value = finding.value as unknown as RoomFindingValue;
  const set = (patch: Partial<RoomFindingValue>) => onChange({ ...value, ...patch });
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium">
          {value.name}
          {finding.edited ? (
            <span className="ml-2 text-xs text-[color:var(--color-muted)]">{t('photoQuotes.review.edited')}</span>
          ) : null}
        </p>
        <span className="text-xs text-[color:var(--color-muted)]">
          {t('photoQuotes.review.confidence', { percent: Math.round(finding.confidence * 100) })}
        </span>
      </div>
      <EvidenceChips ids={finding.evidencePhotoIds} photoLabel={photoLabel} />
      <ExtraTasks
        roomTemplateId={value.roomTemplateId}
        ids={value.serviceTemplateIds}
        editable={editable}
        onRemove={(id) => set({ serviceTemplateIds: value.serviceTemplateIds.filter((other) => other !== id) })}
        onAdd={(id) => set({ serviceTemplateIds: [...value.serviceTemplateIds, id] })}
      />
      <div className="mt-2 grid grid-cols-[repeat(auto-fit,minmax(min(100%,10rem),1fr))] gap-2">
        <TextInput
          // Re-mounted when the server's count changes, so a refused edit
          // shows the stored number again rather than the one typed.
          key={value.count}
          label={t('photoQuotes.review.count')}
          type="number"
          min={0}
          max={30}
          disabled={!editable}
          defaultValue={value.count}
          onBlur={(event) => {
            const count = Number(event.target.value);
            if (Number.isInteger(count) && count >= 0 && count <= 30 && count !== value.count) set({ count });
          }}
        />
        <Select
          label={t('photoQuotes.review.size')}
          value={value.sizeCode}
          disabled={!editable}
          options={ROOM_SIZE_CODES.map((code) => ({ value: code, label: t(`room.size.${code}`) }))}
          onChange={(event) => set({ sizeCode: event.target.value as RoomFindingValue['sizeCode'] })}
        />
        <Select
          label={t('photoQuotes.review.condition')}
          value={value.conditionCode}
          disabled={!editable}
          options={ROOM_CONDITION_CODES.map((code) => ({ value: code, label: t(`room.condition.${code}`) }))}
          onChange={(event) => set({ conditionCode: event.target.value as RoomFindingValue['conditionCode'] })}
        />
      </div>
    </li>
  );
}

/**
 * Tasks on top of the room's standard clean (inside the oven, the blinds):
 * the ones the photos called for, and any the reviewer adds from the room's
 * own list when the photos missed one. Each one is in the price, so each one
 * is named here and can be taken off before anything is sent.
 */
function ExtraTasks({
  roomTemplateId,
  ids,
  editable,
  onRemove,
  onAdd,
}: {
  roomTemplateId: string;
  ids: readonly string[];
  editable: boolean;
  onRemove: (id: string) => void;
  onAdd: (id: string) => void;
}) {
  const { t } = useTranslation();
  const library = useQuery({ queryKey: ['templates', 'services', 'all'], queryFn: () => templateApi.services(true) });
  const rooms = useQuery({ queryKey: ['templates', 'rooms'], queryFn: () => templateApi.rooms(), enabled: editable });
  const serviceOf = (id: string) => library.data?.items.find((service) => service.id === id);
  const nameOf = (id: string) => serviceOf(id)?.name ?? t('photoQuotes.review.unknownTask');
  // Only the room's own optional tasks: the defaults are always in its price,
  // and a task from another room's list would not be priced on this one.
  const addable = editable
    ? (rooms.data?.items.find((room) => room.id === roomTemplateId)?.services ?? [])
        .filter((link) => !link.defaultSelected && !ids.includes(link.serviceTemplateId))
        .map((link) => serviceOf(link.serviceTemplateId))
        .filter((service) => service !== undefined && service.isActive !== false)
    : [];
  if (ids.length === 0 && addable.length === 0) return null;
  return (
    <div className="mt-2">
      {ids.length > 0 ? (
        <p className="text-xs text-[color:var(--color-muted)]">{t('photoQuotes.review.extraTasks')}</p>
      ) : null}
      <ul className={ids.length > 0 ? 'mt-1 flex flex-wrap gap-1.5' : 'hidden'}>
        {ids.map((id) => (
          <li key={id} className="badge inline-flex items-center gap-1 text-sm">
            {nameOf(id)}
            {editable ? (
              <button
                type="button"
                className="rounded px-1 text-[color:var(--color-muted)] hover:text-[color:var(--color-ink)]"
                aria-label={t('photoQuotes.review.removeTask', { name: nameOf(id) })}
                onClick={() => onRemove(id)}
              >
                ×
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      {addable.length > 0 ? (
        <Select
          label={t('photoQuotes.review.addTask')}
          value=""
          options={[
            { value: '', label: t('photoQuotes.review.addTaskChoose') },
            ...addable.map((service) => ({ value: service!.id, label: service!.name })),
          ]}
          onChange={(event) => {
            if (event.target.value) onAdd(event.target.value);
          }}
        />
      ) : null}
    </div>
  );
}

/** A room the photos missed and the owner knows is there, from the company's own library. */
function AddRoomForm({ busy, onAdd }: { busy: boolean; onAdd: (value: RoomFindingValue) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [roomId, setRoomId] = useState('');
  const [count, setCount] = useState(1);
  const library = useQuery({ queryKey: ['templates', 'rooms'], queryFn: () => templateApi.rooms(), enabled: open });

  if (!open) {
    return (
      <p className="mt-3">
        <Button variant="ghost" onClick={() => setOpen(true)}>
          {t('photoQuotes.review.addRoom')}
        </Button>
      </p>
    );
  }
  const rooms = (library.data?.items ?? []).filter((room) => room.isActive);
  const chosen = rooms.find((room) => room.id === roomId);
  return (
    <div className="mt-3 rounded border border-[color:var(--color-line)] p-3">
      <Select
        label={t('photoQuotes.review.addRoomType')}
        value={roomId}
        options={[
          { value: '', label: t('photoQuotes.review.addRoomChoose') },
          ...rooms.map((room) => ({ value: room.id, label: room.name })),
        ]}
        onChange={(event) => setRoomId(event.target.value)}
      />
      <TextInput
        label={t('photoQuotes.review.count')}
        type="number"
        min={1}
        max={30}
        value={count}
        onChange={(event) => setCount(Math.max(1, Math.min(30, Number(event.target.value) || 1)))}
      />
      <div className="flex gap-2">
        <Button
          variant="primary"
          busy={busy}
          disabled={!chosen}
          onClick={() => {
            if (!chosen) return;
            onAdd({
              roomTemplateId: chosen.id,
              name: chosen.name,
              count,
              photographedCount: 0,
              sizeCode: 'medium',
              conditionCode: 'average',
              serviceTemplateIds: [],
            });
            setOpen(false);
            setRoomId('');
            setCount(1);
          }}
        >
          {t('photoQuotes.review.addRoomConfirm')}
        </Button>
        <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
      </div>
    </div>
  );
}

const RISK_COMPONENTS = Object.keys(PHOTO_QUOTE_RISK_WEIGHTS) as PhotoQuoteRiskComponent[];

/** The score's breakdown, the preset's lines, and what blocks a clean answer. */
function RiskPanel({ intake }: { intake: PhotoIntakeDetail }) {
  const { t } = useTranslation();
  const risk = intake.risk!;
  return (
    <div className="card">
      <h2 className="mb-2 text-lg font-semibold">{t('photoQuotes.review.riskHeading')}</h2>
      <p className="mb-2 flex flex-wrap items-center gap-2">
        <RiskBadge level={risk.level} />
        <span className="text-sm text-[color:var(--color-muted)]">
          {t('photoQuotes.review.riskScore', {
            score: risk.score.toFixed(2),
            glance: risk.thresholds.glanceMaxScore.toFixed(2),
          })}
        </span>
      </p>
      <ul className="space-y-1 text-sm">
        {RISK_COMPONENTS.map((component) => {
          const share = risk.components[component] ?? 0;
          return (
            <li key={component} className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[minmax(0,1fr)_6rem]">
              <span>
                {t(`photoQuotes.riskComponents.${component}`)}
                <span className="ml-1 text-xs text-[color:var(--color-muted)]">
                  {t('photoQuotes.review.weight', { percent: Math.round(PHOTO_QUOTE_RISK_WEIGHTS[component] * 100) })}
                </span>
              </span>
              <span className="h-2 rounded bg-[color:var(--color-line)]" aria-hidden="true">
                <span
                  className="block h-2 rounded bg-[color:var(--color-warning)]"
                  style={{ width: `${Math.round(Math.min(1, share) * 100)}%` }}
                />
              </span>
            </li>
          );
        })}
      </ul>
      {intake.coverage && !intake.coverage.complete ? (
        <p className="mt-3 text-sm">
          {t('photoQuotes.review.coverageMissing', {
            missing: intake.coverage.missing
              .map((gap) =>
                gap.kind === 'kitchen'
                  ? t('photoQuotes.coverage.kitchen')
                  : t(`photoQuotes.coverage.${gap.kind}`, { count: gap.missing }),
              )
              .join(', '),
          })}
        </p>
      ) : null}
      {intake.reasonCodes.filter((code) => code !== 'auto_firm_off').length > 0 ? (
        <ul className="mt-2 list-disc pl-5 text-sm">
          {intake.reasonCodes
            .filter((code) => code !== 'auto_firm_off')
            .map((code) => (
              <li key={code}>{t(`photoQuotes.reasons.${code}`)}</li>
            ))}
        </ul>
      ) : null}
      {intake.decision ? (
        <p className="mt-2 text-sm text-[color:var(--color-muted)]">{t(`photoQuotes.decision.${intake.decision}`)}</p>
      ) : null}
    </div>
  );
}

function SendDialog({
  intake,
  kind,
  onCancel,
  onSent,
}: {
  intake: PhotoIntakeDetail;
  kind: PhotoQuotePriceKind;
  onCancel: () => void;
  onSent: (result: { intake: PhotoIntakeDetail; quoteId: string; emailed: boolean }) => void;
}) {
  const { t } = useTranslation();
  const { company, can } = useSession();
  const needsHourlyConfirmation = company?.areaPricingRules != null;
  const [confirmHourlyPricing, setConfirmHourlyPricing] = useState(false);
  const queryClient = useQueryClient();
  const [email, setEmail] = useState(intake.email ?? '');
  const [message, setMessage] = useState('');
  const send = useMutation({
    mutationFn: () =>
      photoQuoteApi.send(intake.id, {
        version: intake.findingsVersion,
        kind,
        ...(confirmHourlyPricing ? { confirmHourlyPricing: true } : {}),
        ...(email && email !== intake.email ? { recipientEmail: email } : {}),
        ...(message.trim() ? { message: message.trim() } : {}),
      }),
    onSuccess: onSent,
    // A refused send (the price moved, or somebody changed the photo quote
    // first) has the truth stored: read it again, so this dialog shows that
    // price beside the error and the next confirm sends at its version.
    onError: () => void queryClient.invalidateQueries({ queryKey: ['photo-intake', intake.id] }),
  });
  const preview = intake.preview!;
  return (
    <ConfirmDialog
      title={
        kind === 'firm'
          ? t('photoQuotes.send.confirmFirmTitle', { name: intake.name })
          : t('photoQuotes.send.confirmRangeTitle', { name: intake.name })
      }
      confirmLabel={kind === 'firm' ? t('photoQuotes.send.firm') : t('photoQuotes.send.range')}
      confirmDisabled={
        !can('quotes:write') || !can('customers:write') || !email || (needsHourlyConfirmation && !confirmHourlyPricing)
      }
      busy={send.isPending}
      error={send.error?.message ?? null}
      onConfirm={() => send.mutate()}
      onCancel={onCancel}
    >
      <p className="mb-3">
        {kind === 'firm'
          ? t('photoQuotes.send.firmSummary', { amount: money(preview.totalCents, preview.currencyCode) })
          : t('photoQuotes.send.rangeSummary', {
              low: money(preview.range.lowCents, preview.currencyCode),
              high: money(preview.range.highCents, preview.currencyCode),
            })}
      </p>
      <TextInput
        label={t('photoQuotes.send.recipient')}
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
      />
      {needsHourlyConfirmation && (
        <Checkbox
          checked={confirmHourlyPricing}
          label={t('areaPricing.photoHourlyConfirmation')}
          onChange={(event) => setConfirmHourlyPricing(event.target.checked)}
        />
      )}
      <TextArea
        label={t('photoQuotes.send.message')}
        rows={3}
        maxLength={1000}
        value={message}
        onChange={(event) => setMessage(event.target.value)}
      />
    </ConfirmDialog>
  );
}

function AskMoreDialog({
  intake,
  onCancel,
  onDone,
}: {
  intake: PhotoIntakeDetail;
  onCancel: () => void;
  onDone: (detail: PhotoIntakeDetail) => void;
}) {
  const { t } = useTranslation();
  const { can } = useSession();
  const queryClient = useQueryClient();
  // Pre-ticked from what the coverage check found missing.
  const [needed, setNeeded] = useState<Set<PhotoRequestNeed>>(() => {
    const initial = new Set<PhotoRequestNeed>();
    for (const gap of intake.coverage?.missing ?? []) {
      initial.add(gap.kind === 'kitchen' ? 'kitchen' : gap.kind === 'bathroom' ? 'bathrooms' : 'bedrooms');
    }
    return initial;
  });
  const [message, setMessage] = useState('');
  const ask = useMutation({
    mutationFn: () =>
      photoQuoteApi.askMore(intake.id, { needed: [...needed], ...(message.trim() ? { message: message.trim() } : {}) }),
    onSuccess: onDone,
    // A refused ask can still mean the photo quote moved on (somebody else
    // asked, or the customer is already answering one): read it again.
    onError: () => void queryClient.invalidateQueries({ queryKey: ['photo-intake', intake.id] }),
  });
  return (
    <ConfirmDialog
      title={t('photoQuotes.askMore.title', { name: intake.name })}
      confirmLabel={t('photoQuotes.askMore.confirm')}
      confirmDisabled={!can('quotes:write') || (needed.size === 0 && !message.trim())}
      busy={ask.isPending}
      error={ask.error?.message ?? null}
      onConfirm={() => ask.mutate()}
      onCancel={onCancel}
    >
      <p className="mb-3 text-sm">{t('photoQuotes.askMore.body', { count: intake.morePhotoRequestsLeft })}</p>
      {PHOTO_REQUEST_NEEDS.map((need) => (
        <Checkbox
          key={need}
          label={t(`photoQuotes.needs.${need}`)}
          checked={needed.has(need)}
          onChange={(event) =>
            setNeeded((current) => {
              const next = new Set(current);
              if (event.target.checked) next.add(need);
              else next.delete(need);
              return next;
            })
          }
        />
      ))}
      <TextArea
        label={t('photoQuotes.askMore.message')}
        rows={3}
        maxLength={1000}
        value={message}
        onChange={(event) => setMessage(event.target.value)}
      />
    </ConfirmDialog>
  );
}

function WalkthroughDialog({
  intake,
  onCancel,
  onDone,
}: {
  intake: PhotoIntakeDetail;
  onCancel: () => void;
  onDone: (detail: PhotoIntakeDetail) => void;
}) {
  const { t } = useTranslation();
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const walk = useMutation({
    mutationFn: () =>
      photoQuoteApi.walkthrough(intake.id, {
        version: intake.findingsVersion,
        ...(message.trim() ? { message: message.trim() } : {}),
      }),
    onSuccess: onDone,
    // Refused because the photo quote moved on (an ask for more photos, an
    // edit): read it again, so the next confirm is made on what it is now.
    onError: () => void queryClient.invalidateQueries({ queryKey: ['photo-intake', intake.id] }),
  });
  return (
    <ConfirmDialog
      title={t('photoQuotes.walkthrough.title', { name: intake.name })}
      confirmLabel={t('photoQuotes.walkthrough.confirm')}
      confirmDisabled={!can('quotes:write')}
      busy={walk.isPending}
      error={walk.error?.message ?? null}
      onConfirm={() => walk.mutate()}
      onCancel={onCancel}
    >
      <p className="mb-3 text-sm">{t('photoQuotes.walkthrough.body')}</p>
      <TextArea
        label={t('photoQuotes.walkthrough.message')}
        rows={3}
        maxLength={1000}
        value={message}
        onChange={(event) => setMessage(event.target.value)}
      />
    </ConfirmDialog>
  );
}

function RereadDialog({
  intake,
  onCancel,
  onDone,
}: {
  intake: PhotoIntakeDetail;
  onCancel: () => void;
  onDone: (detail: PhotoIntakeDetail) => void;
}) {
  const { t } = useTranslation();
  const { can } = useSession();
  // Reading sends the photos to Anthropic: the AI notice is accepted first,
  // as before any other AI action.
  const disclosure = useAiDisclosure();
  const reread = useMutation({
    mutationFn: async () => {
      await disclosure.ensure(true);
      return photoQuoteApi.analyze(intake.id, intake.findingsVersion);
    },
    onSuccess: onDone,
  });
  return (
    <>
      <ConfirmDialog
        title={t('photoQuotes.reread.title')}
        confirmLabel={t('photoQuotes.reread.confirm')}
        confirmDisabled={!can('quotes:write')}
        busy={reread.isPending}
        error={reread.error && reread.error.message !== 'declined' ? reread.error.message : null}
        onConfirm={() => reread.mutate()}
        onCancel={onCancel}
      >
        <p className="text-sm">{t('photoQuotes.reread.body')}</p>
      </ConfirmDialog>
      {disclosure.dialog}
    </>
  );
}
